import React, { useState } from 'react';
import type { ChatMessage } from '@/messages/messageSlice';
import ReactMarkdown from "react-markdown";
import { TextSearch } from 'lucide-react';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

interface MessageBubbleProps {
  message: ChatMessage;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const [showSql, setShowSql] = useState(false);
  const hasSql = !!message.sqlQuery;

  return (
    <div
      className={`max-w-[75%] px-3 py-2 rounded-xl transition-all animate-fadeIn shadow-sm
        ${ isUser ? 'ml-auto bg-blue-700 text-white' : 'mr-auto bg-slate-50 text-slate-800 border border-slate-200'}
      `}
    >
      <div className="text-sm leading-relaxed">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks]}
          components={{
            table: ({ children }) => (
              <div className="w-full overflow-x-auto my-2">
                <table className="w-full table-auto border-collapse border-slate-300 divide-y divide-slate-300">
                  {children}
                </table>
              </div>
            ),
            thead: ({ children }) => (
              <thead className="bg-slate-200 text-sm text-slate-700">
                {children}
              </thead>
            ),
            th: ({ children }) => (
              <th className="px-3 py-2 text-sm text-left font-semibold border border-slate-300 bg-slate-200 text-slate-700">
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="px-3 py-2 text-sm border border-slate-300 text-slate-700">
                {children}
              </td>
            ),
            tr: ({ children }) => (
              <tr className="even:bg-slate-200 text-sm hover:bg-slate-300 transition-colors">
                {children}
              </tr>
            ),
          }}
      >
          {message.text}
        </ReactMarkdown>
      </div>

      {hasSql && (
        <div className="flex justify-end mt-2">
          {hasSql && (
            <button
              onClick={() => setShowSql(!showSql)}
              className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg ${showSql ? 'bg-slate-200' : 'bg-slate-100'} hover:bg-slate-300 text-slate-700`}
              aria-expanded={showSql}
              aria-label="Toggle SQL query"
            >
              Data Query
              <TextSearch size={12} />
            </button>
          )}
        </div>
      )}

      {showSql && hasSql && (
        <pre className="mt-2 p-2 rounded-md text-xs overflow-x-auto font-mono whitespace-pre-wrap break-words bg-blue-900 text-slate-100">
          {message.sqlQuery}
        </pre>
      )}
    </div>
  );
};

export default MessageBubble;